/**
 * landing.ts — the room-code + display-name form, dressed as a torchlit wall.
 *
 * Shown when no room is in the URL, when a `/room/:code` link needs a name, or
 * after a refused join. No browser storage is used (AGENTS.md), so the name
 * lives in memory only.
 *
 * The scene covers the arena rather than floating over it: a cobblestone wall
 * generated at runtime (`cobblestone.ts`), a torch either side, and the site
 * name above the panel. Everything is CSS and one generated SVG — no fonts,
 * images or audio to fetch, so a fresh deploy cannot 404 the front door.
 *
 * The form contract is unchanged: same ids, same single-promise lifecycle. The
 * whole scene is one `<form>`, so Enter still submits from either field.
 */

import {
  DEFAULT_NAME,
  NAME_MAX,
  ROOM_CODE_MAX,
  isValidRoomCode,
  normalizeRoomCode,
} from '@dueler/shared';
import { cobbleSvg } from './cobblestone.js';
// The landing's faces, base64-inlined so they cannot 404. Imported here rather
// than linked from index.html so the bundler owns them and they ship with this
// view — the in-game HUD keeps the monospace it needs for tabular readouts.
import './vendor/fonts.css';

/** The site, not the game: this is the front door of chrysamere.online. */
const SITE_NAME = 'Chrysamere';
const SITE_TLD = '.online';

export interface LandingResult {
  code: string;
  name: string;
}

export function randomRoomCode(): string {
  // 6 chars from the a-z0-9 alphabet room codes allow.
  let out = '';
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < 6; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

/**
 * A wall-mounted torch: four nested flame bands, then the haft and its ring.
 *
 * Order matters — the haft is drawn after the fire so it covers the flame's base.
 */
function torchHtml(side: 'left' | 'right'): string {
  return `
    <div class="torch torch-${side}" aria-hidden="true">
      <div class="torch-fire">
        <div class="flame flame-outer"></div>
        <div class="flame flame-mid"></div>
        <div class="flame flame-core"></div>
        <div class="flame flame-hot"></div>
      </div>
      <div class="torch-haft"></div>
      <div class="torch-bracket"></div>
    </div>
  `;
}

/** Resolves with a valid code + name once the player submits the form. */
export function showLanding(prefill: { code?: string; name?: string; error?: string }): Promise<LandingResult> {
  const form = document.createElement('form');
  form.className = 'landing-scene';
  form.innerHTML = `
    <div class="wall" aria-hidden="true">${cobbleSvg()}</div>
    <div class="vignette" aria-hidden="true"></div>
    ${torchHtml('left')}
    <div class="landing-col">
      <div class="brand">
        <h1 class="brand-name">${SITE_NAME}</h1>
        <div class="brand-tld">${SITE_TLD}</div>
      </div>
      <div class="panel landing">
        <p class="muted">Enter a room code to duel. Share the code — or the URL — with your opponent.</p>
        <label>Room code<input id="code" maxlength="${ROOM_CODE_MAX}" autocomplete="off" spellcheck="false" /></label>
        <label>Display name<input id="name" maxlength="${NAME_MAX}" autocomplete="off" spellcheck="false" /></label>
        <div class="row">
          <button id="join" type="submit">Join</button>
          <button id="random" type="button" class="secondary">Random code</button>
        </div>
        <p class="error" id="error"></p>
      </div>
    </div>
    ${torchHtml('right')}
  `;

  document.body.appendChild(form);

  const codeInput = form.querySelector<HTMLInputElement>('#code')!;
  const nameInput = form.querySelector<HTMLInputElement>('#name')!;
  const errorEl = form.querySelector<HTMLParagraphElement>('#error')!;

  codeInput.value = prefill.code ?? randomRoomCode();
  nameInput.value = prefill.name ?? DEFAULT_NAME;
  errorEl.textContent = prefill.error ?? '';

  form.querySelector<HTMLButtonElement>('#random')!.addEventListener('click', () => {
    codeInput.value = randomRoomCode();
  });

  // Focus whichever field the player still has to fill in.
  (prefill.code !== undefined ? nameInput : codeInput).focus();
  nameInput.select();

  return new Promise<LandingResult>((resolve) => {
    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      const code = normalizeRoomCode(codeInput.value);
      if (!isValidRoomCode(code)) {
        errorEl.textContent = 'Room codes are 3–12 letters or digits.';
        return;
      }
      form.remove();
      resolve({ code, name: nameInput.value });
    });
  });
}
